import { SHEET_MAX_PIXELS, SHEET_MAX_SIDE, SHEET_MIN_SIDE, SHEET_WARN_PIXELS } from '../../doc/schema';

/**
 * Sketch sheet formats. Paper presets are physical sizes rendered at a chosen dpi; screen and
 * square presets are pixel sizes (their dpi is 96, so they land on the board at 1:1).
 */

export type PresetGroup = 'iso' | 'us' | 'screen' | 'square';

export interface SheetPreset {
  id: string;
  name: string;
  group: PresetGroup;
  /** Portrait size in millimetres (paper), or … */
  mm?: [number, number];
  /** … a fixed pixel size (screens). */
  px?: [number, number];
}

export const SHEET_PRESETS: SheetPreset[] = [
  { id: 'a3', name: 'A3', group: 'iso', mm: [297, 420] },
  { id: 'a4', name: 'A4', group: 'iso', mm: [210, 297] },
  { id: 'a5', name: 'A5', group: 'iso', mm: [148, 210] },
  { id: 'a6', name: 'A6', group: 'iso', mm: [105, 148] },
  { id: 'letter', name: 'Letter', group: 'us', mm: [215.9, 279.4] },
  { id: 'legal', name: 'Legal', group: 'us', mm: [215.9, 355.6] },
  { id: 'tabloid', name: 'Tabloid', group: 'us', mm: [279.4, 431.8] },
  { id: 'fhd', name: '1920 × 1080', group: 'screen', px: [1920, 1080] },
  { id: 'qhd', name: '2560 × 1440', group: 'screen', px: [2560, 1440] },
  { id: 'uhd', name: '3840 × 2160', group: 'screen', px: [3840, 2160] },
  { id: 'sq2k', name: '2048 × 2048', group: 'square', px: [2048, 2048] },
  { id: 'sq4k', name: '4096 × 4096', group: 'square', px: [4096, 4096] },
];

export const PRESET_GROUP_NAME: Record<PresetGroup, string> = { iso: 'ISO', us: 'US', screen: 'Screen', square: 'Square' };

export const SHEET_DPIS = [72, 96, 150, 300, 600];
export const DEFAULT_SHEET_PRESET = 'a4';
export const DEFAULT_SHEET_DPI = 300;

export const presetById = (id: string): SheetPreset | undefined => SHEET_PRESETS.find((p) => p.id === id);

/** Is the preset a physical paper size (takes a dpi and an orientation)? */
export const isPaperPreset = (p: SheetPreset | undefined): boolean => !!p?.mm;

export interface SheetSpec {
  format: string;
  pxW: number;
  pxH: number;
  dpi: number;
}

export const mmToPx = (mm: number, dpi: number): number => Math.round((mm / 25.4) * dpi);
export const inToPx = (inches: number, dpi: number): number => Math.round(inches * dpi);

/** Pixel size for a preset (or the custom size), orientation and dpi. */
export function sheetSpec(
  format: string,
  opts: { dpi: number; landscape: boolean; customW: number; customH: number },
): SheetSpec {
  const preset = presetById(format);
  let pxW: number;
  let pxH: number;
  let dpi = opts.dpi;
  if (preset?.mm) {
    pxW = mmToPx(preset.mm[0], dpi);
    pxH = mmToPx(preset.mm[1], dpi);
    if (opts.landscape) [pxW, pxH] = [pxH, pxW];
  } else if (preset?.px) {
    [pxW, pxH] = preset.px;
    if (opts.landscape !== pxW < pxH) [pxW, pxH] = [pxH, pxW];
    dpi = 96;
  } else {
    pxW = opts.customW;
    pxH = opts.customH;
  }
  return { format: preset ? preset.id : 'custom', pxW: clampSide(pxW), pxH: clampSide(pxH), dpi };
}

export const clampSide = (n: number): number => Math.max(SHEET_MIN_SIDE, Math.min(SHEET_MAX_SIDE, Math.round(Number.isFinite(n) ? n : SHEET_MIN_SIDE)));

/** Why a size cannot be used, a warning about its cost, or null when it is fine. */
export function sheetSizeIssue(pxW: number, pxH: number): { level: 'error' | 'warn'; text: string } | null {
  const px = pxW * pxH;
  if (pxW > SHEET_MAX_SIDE || pxH > SHEET_MAX_SIDE) return { level: 'error', text: `At most ${SHEET_MAX_SIDE} px per side` };
  if (px > SHEET_MAX_PIXELS) return { level: 'error', text: `At most ${Math.round(SHEET_MAX_PIXELS / 1e6)} Mpx per sheet` };
  if (px > SHEET_WARN_PIXELS) return { level: 'warn', text: `${(px / 1e6).toFixed(1)} Mpx: heavy on phones and slow to save` };
  return null;
}

/** Board size of a sheet: its physical size at 96 px per inch, so a 300 dpi sheet is 1:1 at ~312 % zoom. */
export function sheetBoardSize(pxW: number, pxH: number, dpi: number): { w: number; h: number } {
  const k = 96 / Math.max(1, dpi);
  return { w: Math.max(8, Math.round(pxW * k)), h: Math.max(8, Math.round(pxH * k)) };
}

/** Human label: "A4 · 300 dpi · 2480 × 3508 px". */
export function sheetLabel(spec: { format: string; pxW: number; pxH: number; dpi: number }): string {
  const preset = presetById(spec.format);
  const name = preset ? preset.name : 'Custom';
  const dpi = preset?.mm || !preset ? ` · ${spec.dpi} dpi` : '';
  return `${name}${dpi} · ${spec.pxW} × ${spec.pxH} px`;
}
